import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Heart, Menu, X, ChevronDown, CheckCircle, Crown, MapPin, BarChart3, Download, Compass, Backpack, BookOpen } from 'lucide-react';
import { useState } from 'react';
import UserMenu from './UserMenu';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isPremium = isAuthenticated && user?.membership_tier && user.membership_tier !== 'free';

  const exploreItems = [
    { name: 'Walks Gallery', href: '/walks', icon: Compass, iconColor: 'text-amber-500', subtitle: 'Browse all urban walks' },
    { name: 'Gear We Love', href: '/gear', icon: Backpack, iconColor: 'text-amber-500', subtitle: 'Recommended walking gear' },
  ];

  const myWalksItems = [
    { name: 'Favorites', href: '/favorites', icon: Heart, iconColor: 'text-amber-500', subtitle: 'Walks you\'ve saved' },
    { name: 'Completed Walks', href: '/account/completions', icon: CheckCircle, iconColor: 'text-amber-500', subtitle: 'Your walking history' },
  ];

  const premiumFeatureItems = [
    { name: 'GPS Tracking', href: '/tracking/live', icon: MapPin, iconColor: 'text-amber-500', subtitle: 'Track your walks live' },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, iconColor: 'text-amber-500', subtitle: 'Insights into your habits' },
    { name: 'Offline Maps', href: '/offline', icon: Download, iconColor: 'text-amber-500', subtitle: 'Walk without signal' },
    { name: 'Walking Journal', href: '/journals', icon: BookOpen, iconColor: 'text-amber-500', subtitle: 'Your private walk diary' },
  ];

  const mobileNavigation = [
    ...exploreItems,
    ...myWalksItems,
    ...premiumFeatureItems,
    { name: 'Tips', href: '/tips' },
    ...(!isPremium ? [{ name: 'Get Wanderloop+', href: '/membership' }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group flex-shrink-0">
            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center group-hover:bg-gray-800 transition-colors">
              <div className="w-4 h-4 border-2 border-white rounded-full"></div>
            </div>
            <span className="text-xl font-bold text-black group-hover:text-gray-800 transition-colors">
              WANDERLOOP
            </span>
          </Link>

          {/* Login Status Indicator */}
          <div className="hidden sm:flex items-center">
            {!isAuthenticated && (
              <Badge variant="outline" className="text-xs">
                Guest Mode
              </Badge>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <nav className="flex items-center space-x-1">
              {/* Explore Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center"
                  >
                    Explore
                    <ChevronDown className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {exploreItems.map((item) => (
                    <DropdownMenuItem key={item.name} asChild>
                      <Link
                        to={item.href}
                        className="w-full px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                      >
                        {item.icon && <item.icon className={`w-4 h-4 ${item.iconColor || 'text-gray-400'}`} />}
                        <div>
                          <div>{item.name}</div>
                          <div className="text-xs text-gray-400">{item.subtitle}</div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* My Walks Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center"
                  >
                    My Walks
                    <ChevronDown className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {myWalksItems.map((item) => (
                    <DropdownMenuItem key={item.name} asChild>
                      <Link
                        to={item.href}
                        className="w-full px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                      >
                        {item.icon && <item.icon className={`w-4 h-4 ${item.iconColor || 'text-gray-400'}`} />}
                        <div>
                          <div>{item.name}</div>
                          {item.subtitle && <div className="text-xs text-gray-400">{item.subtitle}</div>}
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Premium Features Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center"
                  >
                    Premium Features
                    <ChevronDown className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {premiumFeatureItems.map((item) => (
                    <DropdownMenuItem key={item.name} asChild>
                      <Link
                        to={item.href}
                        className="w-full px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                      >
                        {item.icon && <item.icon className={`w-4 h-4 ${item.iconColor || 'text-gray-400'}`} />}
                        <div>
                          <div>{item.name}</div>
                          <div className="text-xs text-gray-400">{item.subtitle}</div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Tips Link */}
              <Link to="/tips">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-sm font-medium ${
                    location.pathname === '/tips'
                      ? 'text-amber-600'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Tips
                </Button>
              </Link>

              {/* Get Wanderloop+ Button */}
              <Link to="/membership" className="ml-2">
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black px-4 py-2 text-sm font-bold"
                >
                  <Crown className="mr-1 h-4 w-4" />
                  Get Wanderloop+
                </Button>
              </Link>
            </nav>

            <UserMenu />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
              {mobileNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium rounded-md transition-colors ${
                    location.pathname === item.href
                      ? 'text-amber-600 bg-amber-50'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {/* Mobile User Menu */}
              <div className="pt-4 border-t border-gray-200">
                {isAuthenticated ? (
                  <div className="px-3 py-2">
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      Signed in as {user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'User'}
                    </Badge>
                  </div>
                ) : (
                  <div className="space-y-2 px-3">
                    <Link
                      to="/auth?mode=signin"
                      className="block w-full text-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/auth?mode=signup"
                      className="block w-full text-center px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
